import { hexLen, hexToWorld, type Hex } from '../hex.ts';
import { idiv } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';

/**
 * The monastery of the waterfall (docs/sendas/paladin.md, chapter 4; §4, «Las huellas» and «El monasterio»): a
 * monastery of pale stone at the foot of a waterfall, in a gorge. One board for the three acts, so the place is learned:
 *
 * - The gorge runs along y (x right, y down): south of the monastery's front door (+y) its floor goes down toward the
 *   Way, narrowing between its cliffs; the Marea of the memory comes up it (act 1).
 * - Behind the door, the patio (its west end the refectory, with the salt sacks; its east end a ledge over the ravine,
 *   the barranco). North of the patio, two long corridors (west, A; east, C) run up to the chapel's flanks, and a third
 *   (the north gallery, B) crosses both: the two crossings. Between A, B, C and the patio, the cloister, open to the
 *   corridors by its arcades, its cistern in the middle (the sixth altar at its bottom). North of B, the chapel with the
 *   fifth altar, its one door on B. Along the outer side of A and C, the cells: two on each arm of each corridor (eight),
 *   their doors on the corridor.
 * - The walls are thick (a hex at least: nothing slips through them on the board's cells); the cells and the cistern are
 *   rock (nobody walks into them).
 *
 * Integers only. The seed picks the cell the spectres come from (act 2), how much the orchards take and when the siege
 * comes back (act 3); the board is the same for every seed (the renderer's scenes draw it from any).
 */

export const MON_RADIUS = 14;
/** The front door (the gap in the patio's south wall) and the wall's band (y). */
export const MON_DOOR = 1900;
export const PATIO_N = -800;
export const PATIO_S = 800;
export const PATIO_HALF = 6800;
/** The corridors A and C: their middle line (|x|), their half width, their north end. */
export const CORR_X = 3600;
export const CORR_HALF = 800;
export const CORR_N = -9000;
/** The north gallery (B): its middle line (y) and half width. */
export const GALLERY_Y = -5000;
export const GALLERY_HALF = 800;
/** The chapel: half its width, its north end, the wall it shares with B (y), its door's half width. */
export const CHAPEL_HALF = 1700;
export const CHAPEL_N = -9600;
export const CHAPEL_S = -6900;
export const CHAPEL_DOOR = 700;
/** The cistern in the cloister's middle (center y and radius). */
export const CISTERN_Y = -2500;
export const CISTERN_R = 1300;
/** The ravine's ledge, east of the patio (|x| from, to). */
export const LEDGE_X = 8200;

/** The gorge's half width at a point south of the door (its cliffs closing in toward the Way). */
export const gorgeHalf = (y: number): number => 4400 - idiv((y - MON_DOOR) * 1400, 10100);

/** Whether a world point is floor (the gorge, the door, the patio and its ledge, the corridors, the cloister, the chapel). */
export const monFloor = (x: number, y: number): boolean => {
  const ax = Math.abs(x);
  // The gorge, outside the door.
  if (y >= MON_DOOR) return y <= 12300 && ax <= gorgeHalf(y);
  // The door through the south wall.
  if (y > PATIO_S) return ax <= 800;
  // The patio (and the ravine's ledge at its east end).
  if (y >= PATIO_N) return ax <= PATIO_HALF || (x > 0 && x <= LEDGE_X && y >= -500 && y <= 500);
  // The corridors A and C, from the patio to their north ends.
  if (y >= CORR_N && ax >= CORR_X - CORR_HALF && ax <= CORR_X + CORR_HALF) return true;
  // The north gallery, B.
  if (y >= GALLERY_Y - GALLERY_HALF && y <= GALLERY_Y + GALLERY_HALF) return ax <= CORR_X + CORR_HALF;
  // The cloister about its cistern.
  if (y > GALLERY_Y + GALLERY_HALF) {
    if (ax > CORR_X - CORR_HALF) return false;
    const dy = y - CISTERN_Y;
    return x * x + dy * dy > CISTERN_R * CISTERN_R;
  }
  // The chapel's door, and the chapel.
  if (y > CHAPEL_S) return ax <= CHAPEL_DOOR;
  return y >= CHAPEL_N && ax <= CHAPEL_HALF;
};

/** Which region a point is in: 0 the gorge, 1 the patio, 2 the corridors, 3 the gallery, 4 the cloister, 5 the chapel (and its door). */
export const monRegion = (x: number, y: number): number => {
  const ax = Math.abs(x);
  if (y >= MON_DOOR) return 0;
  if (y >= PATIO_N) return 1;
  if (y <= CHAPEL_S + 1 && ax <= CHAPEL_HALF) return 5;
  if (y >= GALLERY_Y - GALLERY_HALF && y <= GALLERY_Y + GALLERY_HALF) return 3;
  if (ax >= CORR_X - CORR_HALF) return 2;
  if (y > GALLERY_Y + GALLERY_HALF) return 4;
  return 5;
};

export interface MonasteryLayout {
  rocks: Hex[];
  /** The front door's middle (the threshold) and the people's places. */
  door: { x: number; y: number };
  keeps: Record<string, { x: number; y: number }>;
  /** The fifth altar (the chapel's) and the sixth (at the cistern's bottom). */
  altar5: { x: number; y: number };
  altar6: { x: number; y: number };
  /** The eight cells' doors (on the corridor's outer wall): A north far, near; A south near, far; C the same. */
  cells: { x: number; y: number }[];
  /** Each cell's way to the altar (x, y pairs), from its door. */
  routes: number[][];
  /** Where a handful of salt can be thrown: the two crossings, the four arms between their cells, the chapel's door, B either side of it. */
  spots: { x: number; y: number }[];
  /** The refectory's salt sacks. */
  sack: { x: number; y: number };
  /** The cistern's three gates (the orchards west, the kitchen north, the ravine east). */
  gates: { x: number; y: number }[];
  /** The cell the spectres come from. */
  culprit: number;
  /** How many marks the orchards take (4 to 6). */
  orchards: number;
  /** The siege (act 3): its first band (s), the quiet after it (s), its return (s). */
  siege: { first: number; quiet: number; back: number };
  /** The great sentinel's way up from the ravine to the chapel (x, y pairs). */
  climb: number[];
  /** Where the Marea's bands set foot in the gorge (x of each spawn spot), and each band's size from the act's (−1 to +1). */
  foot: { y: number; xs: number[]; more: number[] };
  /** Where the hero starts in each act (the memory, the cells, the cloister). */
  start: { porch: { x: number; y: number }; cells: { x: number; y: number }; cloister: { x: number; y: number } };
}

const NEAR_N = -6700;
const FAR_N = -8200;
const NEAR_S = -3400;
const FAR_S = -1800;

const build = (seed: number): MonasteryLayout => {
  const rng = seedRng(deriveSeed(seed, 0x30ca));
  const R = MON_RADIUS;
  const rocks: Hex[] = [];
  for (let r = -R - 2; r <= R + 2; r++)
    for (let q = -R - 2; q <= R + 2; q++) {
      const d = hexLen(q, r);
      if (d > R + 2) continue;
      if (d > R) {
        rocks.push({ q, r });
        continue;
      }
      const w = hexToWorld(q, r);
      if (!monFloor(w.x, w.y)) rocks.push({ q, r });
    }
  const cx = CORR_X + CORR_HALF;
  const cells: { x: number; y: number }[] = [];
  for (const side of [-1, 1]) for (const y of [FAR_N, NEAR_N, NEAR_S, FAR_S]) cells.push({ x: side * cx, y });
  // Each cell's way: out of its door to the corridor's middle, along it to its crossing, along B to the chapel's door, in to the altar.
  const routes = cells.map((c) => {
    const sx = c.x < 0 ? -CORR_X : CORR_X;
    return [sx, c.y, sx, GALLERY_Y, 0, GALLERY_Y, 0, CHAPEL_S + 600, 0, -8300];
  });
  const culprit = rngInt(rng, 8);
  const more: number[] = [];
  for (let i = 0; i < 24; i++) more.push(rngInt(rng, 3) - 1);
  return {
    rocks,
    door: { x: 0, y: MON_DOOR },
    keeps: {
      // The memory: Ser Edda on the threshold's left (the squire, the hero, on her right).
      edda: { x: -700, y: MON_DOOR + 900 },
      // Ser Marit in the chapel by the fifth altar (act 2), at the cistern's south lip (act 3).
      marit: { x: 0, y: -7600 },
      maritCloister: { x: 0, y: CISTERN_Y + CISTERN_R + 500 },
      // Brother Tadeo barring the door (act 2), by the fifth altar (act 3).
      tadeo: { x: 0, y: PATIO_S - 300 },
      tadeoChapel: { x: 700, y: -8000 },
    },
    altar5: { x: 0, y: -8700 },
    altar6: { x: 0, y: CISTERN_Y },
    cells,
    routes,
    spots: [
      { x: -CORR_X, y: GALLERY_Y },
      { x: CORR_X, y: GALLERY_Y },
      // (An arm's spot just past its far cell, toward the crossing: the far cell's spectres walk through it, the near one's never.)
      { x: -CORR_X, y: FAR_N + 600 },
      { x: -CORR_X, y: FAR_S - 600 },
      { x: CORR_X, y: FAR_N + 600 },
      { x: CORR_X, y: FAR_S - 600 },
      { x: 0, y: CHAPEL_S + 400 },
      { x: -1800, y: GALLERY_Y },
      { x: 1800, y: GALLERY_Y },
    ],
    sack: { x: -5600, y: 0 },
    gates: [
      { x: -CISTERN_R - 800, y: CISTERN_Y },
      { x: 0, y: CISTERN_Y - CISTERN_R - 600 },
      { x: CISTERN_R + 800, y: CISTERN_Y },
    ],
    culprit,
    orchards: 4 + rngInt(rng, 3),
    siege: { first: 8 + rngInt(rng, 6), quiet: 62 + rngInt(rng, 16), back: 150 + rngInt(rng, 30) },
    climb: [LEDGE_X - 400, 0, CORR_X, 0, CORR_X, GALLERY_Y, 0, GALLERY_Y, 0, CHAPEL_S + 300, 0, -7900],
    foot: { y: 11400, xs: [-1800, -600, 600, 1800], more },
    start: { porch: { x: 700, y: MON_DOOR + 1000 }, cells: { x: -1200, y: 0 }, cloister: { x: 600, y: CISTERN_Y + CISTERN_R + 900 } },
  };
};

const CACHE = new Map<number, MonasteryLayout>();

/** The monastery for a fight's seed. */
export const monasteryLayout = (seed: number): MonasteryLayout => {
  const hit = CACHE.get(seed);
  if (hit) return hit;
  const L = build(seed);
  if (CACHE.size > 64) CACHE.clear();
  CACHE.set(seed, L);
  return L;
};
